/**
 * Component: ErrorBoundary
 * SpecReferenceID: FRONTEND-WEB-V1
 * Requirements: FRONTEND-WEB-V1-R06, FRONTEND-WEB-V1-R14
 *
 * React error boundary component that catches JavaScript errors in child
 * component tree and displays a fallback UI instead of crashing.
 *
 * FRONTEND-WEB-V1-R06: Graceful error handling with user-friendly messages.
 * FRONTEND-WEB-V1-R14: WCAG 2.1 AA compliant accessibility.
 *
 * Risk Links: R8
 * FMEA Links: FM8
 */

import React, { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

/**
 * Props for the ErrorBoundary component.
 */
interface ErrorBoundaryProps {
  /** Child components to render */
  children: ReactNode;
  /** Optional fallback UI to render on error */
  fallback?: ReactNode;
  /** Optional callback when an error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  /** Optional custom error message */
  errorMessage?: string;
}

/**
 * State for the ErrorBoundary component.
 */
interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error fallback component with i18n support.
 * FRONTEND-WEB-V1-R06: User-friendly error display.
 * FRONTEND-WEB-V1-R14: Accessible error messaging.
 */
function ErrorFallback({
  error,
  errorMessage,
  onReset,
}: {
  error: Error | null;
  errorMessage?: string;
  onReset: () => void;
}): React.ReactElement {
  const { t } = useTranslation();

  return (
    <div
      className="flex min-h-[400px] items-center justify-center p-4"
      role="alert"
      aria-live="assertive"
    >
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-destructive">
            {t('errors.serverError', 'Something went wrong')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-foreground">
            {errorMessage || t('errors.serverErrorDescription', 'An unexpected error occurred. Please try again.')}
          </p>
          {import.meta.env.DEV && error && (
            <details className="rounded-md bg-muted p-3 text-sm">
              <summary className="cursor-pointer font-medium">
                Error Details (Development Only)
              </summary>
              <pre className="mt-2 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">
                {error.message}
                {error.stack && `\n\n${error.stack}`}
              </pre>
            </details>
          )}
        </CardContent>
        <CardFooter className="flex gap-2">
          <Button onClick={onReset} variant="default">
            {t('common.retry', 'Try Again')}
          </Button>
          <Button
            onClick={() => window.location.href = '/'}
            variant="outline"
          >
            {t('errors.goHome', 'Go to Home')}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * ErrorBoundary class component.
 *
 * FRONTEND-WEB-V1-R06: Catches JavaScript errors anywhere in child component
 * tree, logs them, and displays a fallback UI instead of crashing.
 *
 * Note: Error boundaries must be class components as there's no hook equivalent
 * for componentDidCatch/getDerivedStateFromError.
 */
class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps & { fallbackComponent: React.ComponentType<{ error: Error | null; errorMessage?: string; onReset: () => void }> },
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps & { fallbackComponent: React.ComponentType<{ error: Error | null; errorMessage?: string; onReset: () => void }> }) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  /**
   * Update state when an error is thrown.
   * FRONTEND-WEB-V1-R06: Graceful error state handling.
   */
  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return {
      hasError: true,
      error,
    };
  }

  /**
   * Log error information for debugging.
   * FRONTEND-WEB-V1-R06: Error logging for diagnostics.
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error to console in development
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    // Update state with error info
    this.setState({ errorInfo });

    // Call optional error callback
    this.props.onError?.(error, errorInfo);

    // In production, you would send this to an error reporting service
    // Example: errorReportingService.captureException(error, { extra: errorInfo });
  }

  /**
   * Reset the error state to allow retry.
   */
  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, fallback, errorMessage, fallbackComponent: FallbackComponent } = this.props;

    if (hasError) {
      // Use custom fallback if provided
      if (fallback) {
        return fallback;
      }

      // Use the fallback component with i18n support
      return (
        <FallbackComponent
          error={error}
          errorMessage={errorMessage}
          onReset={this.handleReset}
        />
      );
    }

    return children;
  }
}

/**
 * ErrorBoundary wrapper component.
 *
 * FRONTEND-WEB-V1-R06: Provides error boundary functionality with i18n support.
 *
 * Usage:
 * ```tsx
 * <ErrorBoundary>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With custom error message
 * <ErrorBoundary errorMessage="Failed to load data">
 *   <DataComponent />
 * </ErrorBoundary>
 *
 * // With custom fallback
 * <ErrorBoundary fallback={<CustomErrorUI />}>
 *   <MyComponent />
 * </ErrorBoundary>
 *
 * // With error callback
 * <ErrorBoundary onError={(error, info) => logToService(error, info)}>
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */
export function ErrorBoundary(props: ErrorBoundaryProps): React.ReactElement {
  return <ErrorBoundaryClass {...props} fallbackComponent={ErrorFallback} />;
}

/**
 * Higher-order component to wrap a component with an error boundary.
 *
 * FRONTEND-WEB-V1-R06: Utility for wrapping components with error handling.
 *
 * Usage:
 * ```tsx
 * const SafeComponent = withErrorBoundary(MyComponent);
 * ```
 */
export function withErrorBoundary<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
): React.FC<P> {
  const displayName = WrappedComponent.displayName || WrappedComponent.name || 'Component';

  const ComponentWithErrorBoundary: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  ComponentWithErrorBoundary.displayName = `withErrorBoundary(${displayName})`;

  return ComponentWithErrorBoundary;
}

export default ErrorBoundary;
